/**
 * Per-user, per-minute quality telemetry.
 *
 * Samples are shaped exactly like `QualitySample` in `contracts/idl/v1/domain.json` and are posted to
 * `POST /v1/analytics/quality` (project Basic auth) or handed to a caller-provided callback. The
 * reporter applies the Agora counting thresholds before recording (video freeze > 600 ms native /
 * > 500 ms web, audio freeze > 200 ms, network delay > 400 ms) and keeps raw durations for the API,
 * which recomputes rates server-side.
 *
 * Delivery is best-effort: batches are retried with exponential backoff, failed batches stay queued
 * for the next flush, and nothing ever throws into the media loop.
 */
import { FREEZE_AUDIO_THRESHOLD_MS, FREEZE_VIDEO_THRESHOLD_MS, LOGIN_5S_MS, NETWORK_DELAY_THRESHOLD_MS, countableFreezeMs, } from '@nimeet/shared/browser';
import { encodeBase64 } from './base64.js';
import { RtcError, normalizeError, normalizeHttpFailure, rtcError } from './errors.js';
import { resolveFetch } from './token-provider.js';
import { systemClock, } from './types.js';
const EMPTY_DIMENSIONS = {
    region: null,
    networkType: null,
    osType: null,
    sdkVersion: null,
    deviceType: null,
    channelSize: null,
};
function sleep(clock, delayMs) {
    return new Promise((resolve) => {
        clock.setTimeout(() => resolve(), delayMs);
    });
}
function clampInt(value, min, max, fallback) {
    if (!Number.isFinite(value))
        return fallback;
    return Math.min(max, Math.max(min, Math.round(value)));
}
function normalizeDimensions(dimensions) {
    return { ...EMPTY_DIMENSIONS, ...(dimensions ?? {}) };
}
export class QualityReporter {
    channelName;
    uid;
    product;
    dimensions;
    clock;
    logger;
    maxBatchSize;
    maxRetries;
    retryDelayMs;
    sink;
    buckets = new Map();
    deferred = [];
    inFlight = null;
    intervalHandle = null;
    intervalMs = 60_000;
    started = false;
    lastFailure = null;
    constructor(options) {
        if (!options.channelName)
            throw rtcError('invalid_channel_name', 'quality reporting needs a channelName');
        if (!Number.isInteger(options.uid) || options.uid < 0) {
            throw rtcError('invalid_parameter', 'quality reporting needs a valid uid');
        }
        this.channelName = options.channelName;
        this.uid = options.uid;
        this.product = options.product ?? 'web';
        this.dimensions = normalizeDimensions(options.dimensions);
        this.clock = options.clock ?? systemClock;
        this.logger = options.logger;
        this.maxBatchSize = clampInt(options.maxBatchSize ?? 100, 1, 5000, 100);
        this.maxRetries = clampInt(options.maxRetries ?? 3, 0, 10, 3);
        this.retryDelayMs = clampInt(options.retryDelayMs ?? 500, 0, 60_000, 500);
        const endpoint = options.endpoint;
        const customSink = options.sink ?? options.postSamples;
        if (customSink) {
            this.sink = customSink;
        }
        else if (endpoint) {
            this.sink = (samples) => this.postSamples(endpoint, samples);
        }
        else {
            throw rtcError('invalid_parameter', 'quality reporting needs either a postSamples callback or an analytics endpoint');
        }
    }
    /** Record the local user's join outcome (called once per successful/failed join). */
    recordJoin(observation) {
        const bucket = this.bucket(this.uid);
        const delay = Math.max(0, Math.round(observation.delayMs));
        bucket.joinAttempt = true;
        bucket.joinSuccess = observation.success;
        bucket.joinDelayMs = observation.success ? delay : null;
        bucket.joinWithin5s = observation.within5s ?? (observation.success && delay <= LOGIN_5S_MS);
    }
    /** Accumulate active media time for one user (drives the freeze-rate denominator). */
    recordActivity(uid, mediaType, durationMs) {
        const duration = this.validDuration(durationMs);
        if (duration === 0)
            return;
        const bucket = this.bucket(uid);
        if (mediaType === 'audio')
            bucket.audioDurationMs += duration;
        else
            bucket.videoDurationMs += duration;
    }
    /** Record one observed freeze; only freezes above the Agora threshold are counted. */
    recordFreeze(uid, mediaType, freezeMs) {
        const duration = this.validDuration(freezeMs);
        if (duration === 0)
            return;
        const threshold = mediaType === 'audio'
            ? FREEZE_AUDIO_THRESHOLD_MS
            : FREEZE_VIDEO_THRESHOLD_MS[this.product === 'native' ? 'native' : 'web'];
        const counted = countableFreezeMs([duration], threshold);
        if (counted === 0)
            return;
        const bucket = this.bucket(uid);
        if (mediaType === 'audio')
            bucket.audioFreezeMs += counted;
        else
            bucket.videoFreezeMs += counted;
    }
    /**
     * Record an average end-to-end delay for a window. Time above the 400 ms threshold accumulates as
     * `networkDelayDurationMs`, while the average is reported in `networkDelayMs`.
     */
    recordNetworkDelay(uid, delayMs, windowMs) {
        if (!Number.isFinite(delayMs) || delayMs < 0)
            return;
        const window = this.validDuration(windowMs);
        if (window === 0)
            return;
        const bucket = this.bucket(uid);
        bucket.networkDelaySum += Math.round(delayMs) * window;
        bucket.networkDelayCount += window;
        if (delayMs > NETWORK_DELAY_THRESHOLD_MS)
            bucket.networkDelayDurationMs += window;
    }
    /**
     * Record the transport's downlink packet-loss fraction (0..1) for one uid. Agora reports packet
     * loss in call details rather than in the four heat-map metrics, so this feeds the quality
     * overview / call inspector as an extension field.
     */
    recordPacketLoss(uid, rate) {
        if (!Number.isFinite(rate) || rate < 0)
            return;
        const bucket = this.bucket(uid);
        bucket.packetLossSum += Math.min(1, rate);
        bucket.packetLossCount += 1;
    }
    /** Start periodic flushing. The whole batch of samples is posted every `intervalMs`. */
    start(intervalMs = 60_000) {
        this.stopInterval();
        this.intervalMs = clampInt(intervalMs, 1_000, 3_600_000, 60_000);
        this.intervalHandle = this.clock.setInterval(() => {
            void this.flush();
        }, this.intervalMs);
        this.started = true;
    }
    getIntervalMs() {
        return this.intervalMs;
    }
    isStarted() {
        return this.started;
    }
    /** Number of samples that would be posted by the next flush. */
    get pendingCount() {
        return this.buckets.size + this.deferred.length;
    }
    get lastError() {
        return this.lastFailure;
    }
    /** Flush pending samples, retrying each batch. Never rejects. */
    async flush() {
        if (this.inFlight)
            return this.inFlight;
        const run = this.doFlush().finally(() => {
            this.inFlight = null;
        });
        this.inFlight = run;
        return run;
    }
    /** Stop periodic flushing and flush whatever is pending. */
    async stop() {
        this.stopInterval();
        await this.flush();
    }
    /** Drop unsent samples (used when a session ends and telemetry is abandoned). */
    clear() {
        this.buckets.clear();
        this.deferred = [];
    }
    stopInterval() {
        if (this.intervalHandle !== null) {
            this.clock.clearInterval(this.intervalHandle);
            this.intervalHandle = null;
        }
        this.started = false;
    }
    validDuration(value) {
        if (!Number.isFinite(value) || value <= 0)
            return 0;
        return Math.round(value);
    }
    bucketStart(now) {
        return new Date(Math.floor(now / 60_000) * 60_000).toISOString();
    }
    bucket(uid) {
        const bucketStart = this.bucketStart(this.clock.now());
        const key = `${uid}|${bucketStart}`;
        let bucket = this.buckets.get(key);
        if (!bucket) {
            bucket = {
                uid,
                bucketStart,
                videoFreezeMs: 0,
                videoDurationMs: 0,
                audioFreezeMs: 0,
                audioDurationMs: 0,
                networkDelaySum: 0,
                networkDelayCount: 0,
                networkDelayDurationMs: 0,
                packetLossSum: 0,
                packetLossCount: 0,
                joinDelayMs: null,
                joinAttempt: false,
                joinSuccess: false,
                joinWithin5s: false,
            };
            this.buckets.set(key, bucket);
        }
        return bucket;
    }
    drainBuckets() {
        if (this.buckets.size === 0)
            return [];
        const samples = [];
        for (const bucket of this.buckets.values())
            samples.push(this.toPayload(bucket));
        this.buckets.clear();
        return samples.sort((left, right) => {
            const byBucket = left.bucketStart.localeCompare(right.bucketStart);
            return byBucket !== 0 ? byBucket : left.uid - right.uid;
        });
    }
    toPayload(bucket) {
        return {
            channelName: this.channelName,
            uid: bucket.uid,
            bucketStart: bucket.bucketStart,
            videoFreezeMs: Math.round(bucket.videoFreezeMs),
            videoDurationMs: Math.round(bucket.videoDurationMs),
            audioFreezeMs: Math.round(bucket.audioFreezeMs),
            audioDurationMs: Math.round(bucket.audioDurationMs),
            networkDelayMs: bucket.networkDelayCount > 0
                ? Math.round(bucket.networkDelaySum / bucket.networkDelayCount)
                : 0,
            networkDelayDurationMs: Math.round(bucket.networkDelayDurationMs),
            ...(bucket.packetLossCount > 0
                ? { packetLossRate: bucket.packetLossSum / bucket.packetLossCount }
                : {}),
            joinDelayMs: bucket.joinSuccess && bucket.joinDelayMs !== null ? bucket.joinDelayMs : null,
            joinAttempt: bucket.joinAttempt,
            joinSuccess: bucket.joinSuccess,
            joinWithin5s: bucket.joinWithin5s,
            dimensions: { ...this.dimensions },
        };
    }
    async doFlush() {
        const pending = [...this.deferred, ...this.drainBuckets()];
        this.deferred = [];
        if (pending.length === 0)
            return;
        const failed = [];
        for (let offset = 0; offset < pending.length; offset += this.maxBatchSize) {
            const batch = pending.slice(offset, offset + this.maxBatchSize);
            try {
                await this.deliver(batch);
            }
            catch (error) {
                this.lastFailure = normalizeError(error, 'upstream_unavailable');
                failed.push(...batch);
                this.logger?.warn('rtc-web: quality batch delivery failed', this.lastFailure.message);
            }
        }
        if (failed.length > 0)
            this.deferred.push(...failed);
    }
    async deliver(samples) {
        let attempt = 0;
        for (;;) {
            try {
                await this.sink(samples);
                return;
            }
            catch (error) {
                const normalized = normalizeError(error, 'upstream_unavailable');
                this.lastFailure = normalized;
                if (attempt >= this.maxRetries)
                    throw normalized;
                const delay = this.retryDelayMs * 2 ** attempt;
                attempt += 1;
                if (delay > 0)
                    await sleep(this.clock, delay);
            }
        }
    }
    async postSamples(endpoint, samples) {
        const fetchImpl = resolveFetch(endpoint.fetch);
        const url = `${endpoint.baseUrl.replace(/\/+$/, '')}${(endpoint.path ?? '/v1/analytics/quality').replace(/^([^/])/, '/$1')}`;
        const headers = {
            'content-type': 'application/json',
            accept: 'application/json',
        };
        if (endpoint.authorization)
            headers.authorization = endpoint.authorization;
        else if (endpoint.customerId && endpoint.customerSecret) {
            headers.authorization = `Basic ${encodeBase64(`${endpoint.customerId}:${endpoint.customerSecret}`)}`;
        }
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timeoutMs = endpoint.timeoutMs ?? 5000;
        let timedOut = false;
        const timer = controller !== null && timeoutMs > 0
            ? this.clock.setTimeout(() => {
                timedOut = true;
                controller.abort();
            }, timeoutMs)
            : null;
        try {
            const response = await fetchImpl(url, {
                method: 'POST',
                headers,
                body: JSON.stringify({ samples }),
                ...(controller ? { signal: controller.signal } : {}),
            });
            const text = await response.text();
            let parsed = text;
            if (text.length > 0) {
                try {
                    parsed = JSON.parse(text);
                }
                catch {
                    parsed = text;
                }
            }
            if (!response.ok)
                throw normalizeHttpFailure(response.status, parsed, `quality ingestion failed with HTTP ${response.status}`);
        }
        catch (error) {
            if (error instanceof RtcError)
                throw error;
            if (timedOut)
                throw rtcError('upstream_unavailable', `quality ingestion timed out after ${timeoutMs}ms`, {
                    cause: error,
                });
            throw normalizeError(error, 'upstream_unavailable');
        }
        finally {
            if (timer !== null)
                this.clock.clearTimeout(timer);
        }
    }
}
//# sourceMappingURL=quality.js.map